import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Subscription, of } from 'rxjs';
import { ToastService } from 'src/app/service/shared/toast/toast.service';
import { ToolsService } from 'src/app/service/tools/tools.service';
import { SelectedDataModel, TransferModel, TransferModelData } from './tool-transfer.model';

@Injectable({
  providedIn: 'root',
})
export class ToolTransferService {
  private selectedItemsSource = new BehaviorSubject<Array<SelectedDataModel>>([]);
  selectedItems = this.selectedItemsSource.asObservable();

  private modalIsOpenSource = new BehaviorSubject<boolean>(false);
  modalIsOpen = this.modalIsOpenSource.asObservable();

  private submitSubscriberSource = new BehaviorSubject<any>({ next: null, error: null });
  submitSubscriber = this.submitSubscriberSource.asObservable();

  private refreshToolsSubscriberSource = new BehaviorSubject<any>({ next: null, error: null });
  refreshToolsSubscriber = this.refreshToolsSubscriberSource.asObservable();

  defaultTransferModel: TransferModel = {
    toolId: null,
    assignedDate: null,
    notes: null,
  };

  transferModel: TransferModel = this.defaultTransferModel;
  transferSubscription: Subscription;

  constructor(
    private toolsService: ToolsService,
    private toastService: ToastService,
  ) {}

  transfer(item: SelectedDataModel, refreshSubscription: Subscription = undefined) {
    this.transferSubscription = refreshSubscription;
    this.modalIsOpenSource.next(true);
    this.add(item);
  }

  batchTransfer(refreshSubscription: Subscription = undefined) {
    this.transferSubscription = refreshSubscription;
    this.modalIsOpenSource.next(true);
  }

  batchAdd(items: Array<SelectedDataModel>) {
    const currentList = this.selectedItemsSource.value;
    let updatedList = [...currentList, ...items];
    updatedList = this.filterDuplicates(updatedList);
    this.selectedItemsSource.next(updatedList);
    this.saveToLocalStorage();
  }

  batchRemove(items: Array<SelectedDataModel>) {
    const currentList = this.selectedItemsSource.value;
    const updatedList = this.filterRemovable(currentList, items);
    this.selectedItemsSource.next(updatedList);
    this.saveToLocalStorage();
  }

  add(item: SelectedDataModel, promisify: boolean = false) {
    if (promisify) {
      return new Promise((resolve, reject) => {
        this.toolsService.readById
          .tools(item.id, {
            cache: 'clear',
            embed: 'assignment,alert,model',
          })
          .subscribe(
            res => {
              const { data } = res;
              this.remove(data.id);
              const currentList = this.selectedItemsSource.value;
              let updatedList = [data, ...currentList];
              updatedList = this.filterDuplicates(updatedList);
              this.selectedItemsSource.next(updatedList);
              this.saveToLocalStorage();
              resolve(true);
            },
            error => reject(error),
          );
      });
    }

    this.toolsService.readById
      .tools(item.id, {
        cache: 'clear',
        embed: 'assignment,alert,model',
      })
      .subscribe(
        res => {
          const { data } = res;
          this.remove(data.id);
          const currentList = this.selectedItemsSource.value;
          let updatedList = [data, ...currentList];
          updatedList = this.filterDuplicates(updatedList);
          this.selectedItemsSource.next(updatedList);
          this.saveToLocalStorage();
        },
        error => console.error('error coming from subscription', error),
      );
  }

  get(id: number) {
    const currentList = this.selectedItemsSource.value;
    return currentList.find(row => row.id === id);
  }

  remove(id: number) {
    const currentList = this.selectedItemsSource.value;
    const updatedList = currentList.filter(row => row.id !== id);
    this.selectedItemsSource.next(updatedList);
    this.saveToLocalStorage();
  }

  clear() {
    this.clearModelData();
    this.selectedItemsSource.next([]);
    this.saveToLocalStorage();
  }

  saveToLocalStorage() {
    localStorage.setItem('selected-items', JSON.stringify(this.selectedItemsSource.value));
  }

  loadFromLocalStorage() {
    const data = JSON.parse(localStorage.getItem('selected-items')) || [];
    this.selectedItemsSource.next(data);
  }

  filterDuplicates(list: Array<SelectedDataModel>) {
    return list.filter((e, i) => list.findIndex(obj => obj.id == e.id) === i);
  }

  filterRemovable(a: Array<SelectedDataModel>, b: Array<SelectedDataModel>) {
    return a.filter(e => !b.find(f => f.id === e.id));
  }

  async submitTransfer(transferKey: string) {
    if (!this.getIds().length) {
      return this.toastService.showToast('Select at least one tool first', 'error');
    }

    const batchId = await this.toolsService.generateTransferBatchID();

    const modelData = this.getModelData();
    const requests = modelData.map(payload => {
      const queryString = `batchId=${batchId}`;
      return this.toolsService.create['assignment'](payload, queryString);
    });

    forkJoin(requests).subscribe(
      next => this.handleSuccess(next, transferKey),
      error => this.handleError(error),
    );
  }

  handleSuccess(next, transferKey: string) {
    this.refreshToolsSubscriberSource.next({ next });
    this.toastService.showToast('Tools transferred successfully', 'success');
    this.clear();
    if (transferKey === 'close') {
      this.close();
    }
    this.submitSubscriberSource.next({ next });
  }

  handleError(err) {
    this.toastService.showToast('Something went wrong', 'error');
    this.submitSubscriberSource.next({ err });
  }

  addModelData(model: TransferModelData) {
    const { key, value } = model;
    this.transferModel[key] = value;
  }

  removeModelData(model: TransferModelData) {
    const { key } = model;
    delete this.transferModel[key];
  }

  clearModelData() {
    const { assignedDate } = this.transferModel;
    this.transferModel = { ...this.defaultTransferModel };
    this.transferModel.assignedDate = assignedDate;
  }

  getModelData() {
    return this.getIds().map((id: number) => {
      return {
        ...this.transferModel,
        toolId: id,
      };
    });
  }

  getIds() {
    return this.selectedItemsSource.value.map((item: SelectedDataModel) => item.id);
  }

  close() {
    if (this.transferSubscription) this.transferSubscription.unsubscribe();
    this.modalIsOpenSource.next(false);
  }
}
